import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon } from '@cbhq/cds-web/icons';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { Pressable } from '@cbhq/cds-web/system';
import { TextLegal } from '@cbhq/cds-web/typography/TextLegal';

import { useManagedByExplanation } from '../providers/ManagedByExplanationProvider';
import { useProfileDropdownControl } from '../providers/ProfileDropdownControlProvider';
import { useProfileInfo } from '../providers/ProfileMenuV2Provider';

const messages = defineMessages({
  label: {
    defaultMessage: 'Managed by <b>{organizationName}</b>',
    id: 'ManagedByBanner.label',
    description: 'Label showing the organization managing this account',
  },
  accessibilityLabel: {
    defaultMessage: 'Managed by {organizationName}. Tap to learn more',
    id: 'ManagedByBanner.accessibilityLabel',
    description: 'Accessibility label for the managed-by trigger row',
  },
});

export function ManagedByBanner() {
  const { formatMessage } = useIntl();
  const { managedByOrganizationName } = useProfileInfo();
  const { closeMenu } = useProfileDropdownControl();
  const { open: openExplanation } = useManagedByExplanation();

  const handleOpen = useCallback(() => {
    // The explanation renders as a Tray, which cds-web's shared portal host
    // always layers below the dropdown's own overlay — so it can never render
    // on top while the dropdown stays open. Close the dropdown first. Uses
    // ProfileDropdown's own internal ref via context rather than the
    // consumer-supplied profileMenuRef prop, since most hosts (e.g.
    // apps/workspace) don't pass one and that would make this a no-op.
    closeMenu();
    openExplanation();
  }, [closeMenu, openExplanation]);

  if (!managedByOrganizationName) {
    return null;
  }

  return (
    <HStack
      width="100%"
      justifyContent="center"
      alignItems="center"
      gap={1}
      paddingY={2}
      paddingX={3}
      background="bgSecondary"
      borderTopLeftRadius={400}
      borderTopRightRadius={400}
    >
      <Icon name="building" size="s" color="fgMuted" />
      {/* Only the text is the tap target (not the full row) — Pressable has
          no background/radius of its own, so its default press-scale can't
          reveal the panel-corner gap the full-width version had. minWidth={0}
          lifts the flex child's min-width:auto floor so the default
          flex-shrink can still clip long organization names; flexGrow is
          intentionally omitted so a short name stays centered as a group with
          the icon instead of stretching to fill the row. */}
      <Pressable
        testID="managed-by-trigger"
        onClick={handleOpen}
        accessibilityLabel={formatMessage(messages.accessibilityLabel, {
          organizationName: managedByOrganizationName,
        })}
        minWidth={0}
      >
        <TextLegal as="p" overflow="break" numberOfLines={1} color="fgMuted" display="block">
          {formatMessage(messages.label, {
            organizationName: managedByOrganizationName,
            // eslint-disable-next-line react/no-unstable-nested-components
            b: (chunks: ReactNode) => <b>{chunks}</b>,
          })}
        </TextLegal>
      </Pressable>
    </HStack>
  );
}
